import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip, TooltipButton, IconInfo } from '@americanexpress/dls-react';
import { parseHtml } from '../../utils/util';
import { performOperations } from '../../Operations/Operations';

const getVal = require('lodash/get');
/**
 * return single and grouped radio buttons
 * based on isFieldGrouped flag passed
 * @param {*} param0
 */
// eslint-disable-next-line complexity -- desc
const RadioButton = ({
  isFieldGrouped,
  fieldLabel,
  fieldOptions,
  fieldId,
  cssProperties,
  fieldName,
  memberInfo,
  isHiddenRequired = true,
  fieldValidation,
  optionsDependentOn,
  isSymbolRequired,
  filledFormData,
  setFormSubmissionPayload,
  setShowPopUp,
  fieldRank,
  setValidationErrors,
  validationErrors,
  setFocus,
  errors,
  comments,
  setComments,
  entityToEdit,
  entityComments = [],
  entityCompositeKey,
  idsToClear, // to check if we have to clear this field
  operationsToPerform,
  formContainer,
  curPageValues,
  setCurPageValues,
  fieldIdsToClear = [], // to clear the other fields
  dispatchIdsState,
  changePageValues,
  setChangePageValues,
  defaultValue,
  entityData,
  setEntityData,
  keysToUpdate = {},
}) => {
  const [options, setOptions] = useState([...fieldOptions]);
  const [currentSelectedOption, setCurrentSelectedOption] = useState('');
  const [customInvalidMsg, setCustomInvalidMsg] = useState('');
  const [showCustomInvalidMsg, setShowCustomInvalidMsg] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [value, setValue] = useState('No');
  const [checked, setChecked] = useState(false);
  const [valueChanged, setValueChanged] = useState(false);
  const optionalLabel = fieldLabel && fieldLabel.optionalLabel ? fieldLabel.optionalLabel : ' (Optional)';

  const handleOnclick = (e, option) => {
    if (fieldIdsToClear && fieldIdsToClear.length > 0) {
      const clearIds = {};
      fieldIdsToClear.forEach((id, i) => {
        clearIds[id] = i + 1;
      });
      dispatchIdsState({ toggleState: !idsToClear.toggleState, ids: clearIds });
    }
    // delete optionIds from payload
    options.forEach((optn) => {
      delete filledFormData[optn.fieldId];
    });

    if (currentSelectedOption === option.fieldId) {
      setCurrentSelectedOption('NA');
      delete filledFormData[fieldName];
      Object.keys(keysToUpdate).forEach((key) => {
        delete filledFormData[key];
      });
      setFormSubmissionPayload({ ...filledFormData, elemId: null });
      if (fieldRank && errors) {
        if (fieldValidation.required) {
          setValidationError(errors.mandatoryField);
        }
        setValidationErrors({ ...validationErrors, ...{ [fieldRank]: `${option.fieldId}` } });
      }
    } else {
      setCurrentSelectedOption(option.fieldId);
      filledFormData[fieldName] = option.value;
      filledFormData[option.fieldId] = 'Yes';
      setFormSubmissionPayload({ ...filledFormData, elemId: null, ...keysToUpdate });
      if (option.triggerPopUp) {
        setShowPopUp(option.triggerPopUp);
      }
    }
  };
  const handleOnChange = (option) => {
    if (comments && comments[fieldName]) {
      delete comments[fieldName];
      setComments({ ...comments });
    }

    if (operationsToPerform?.onChange?.length > 0) {
      performOperations({
        value: option.value,
        operations: operationsToPerform.onChange,
        dataObject: {
          memberInfo,
          formSubmissionPayload: filledFormData,
          curPageValues,
          setCurPageValues,
          changePageValues,
          setChangePageValues,
          selectedVal: option.value,
          entityData,
          setEntityData,
        },
        functionObject: { setFormSubmissionPayload },
        formContainer,
        setFormSubmissionPayload,
        fieldId: fieldName,
      });
    } else if (option.triggerPopUp) {
      setShowPopUp(option.triggerPopUp);
    }
    // Hide review portal comments when update
    setValueChanged(true);
  };

  useEffect(() => {
    setOptions([...fieldOptions]);
    if (filledFormData && filledFormData[fieldName]) {
      fieldOptions.forEach((opt) => {
        if (opt.value === filledFormData[fieldName]) {
          setCurrentSelectedOption(opt.fieldId);
        }
      });
      // triger the state update to handle back button
      setFormSubmissionPayload({ ...filledFormData, elemId: null });
    } else if (defaultValue) {
      setCurrentSelectedOption(defaultValue);
    }
  }, [fieldName]);

  useEffect(() => {
    if (entityToEdit
      && Object.keys(entityToEdit).length > 0
    ) {
      fieldOptions.forEach((opt) => {
        if (opt.value === entityToEdit[fieldName]) {
          handleOnclick(null, opt);
          handleOnChange(opt);
        }
      });
    }
  }, [entityToEdit, fieldName]);

  const handleInput = () => {
    if (!checked) {
      setChecked(true);
      setValue(fieldLabel.value);
    } else {
      setValue('No');
      setChecked(false);
    }
  };

  useEffect(() => {
    if (memberInfo && optionsDependentOn === 'memberInfo') {
      const newOptions = [];
      options.forEach((currentOption) => {
        const lastChecked = document.querySelector(`#${currentOption.fieldId}`);
        lastChecked.checked = false;
        if (currentOption && currentOption.disabledFor && currentOption.disabledFor.length > 0) {
          let optionDisable = false;
          currentOption.disabledFor.forEach((criteria) => {
            if (criteria === memberInfo.accountTypes.paymentType) {
              optionDisable = true;
            }
          });
          if (optionDisable) {
            const newOption = { ...currentOption, disabled: true };
            newOptions.push(newOption);
          } else {
            newOptions.push({ ...currentOption, disabled: false });
          }
        } else {
          newOptions.push({ ...currentOption, disabled: false });
        }
      });
      setOptions([...newOptions]);
    }
  }, [memberInfo]);

  useEffect(() => {
    if (getVal(idsToClear.ids, fieldName)) {
      setCurrentSelectedOption();
      changePageValues[fieldName] = '';
      setChangePageValues({ ...changePageValues });
      delete idsToClear.ids[fieldName];
      dispatchIdsState({ toggleState: idsToClear.toggleState, ids: idsToClear });
      delete filledFormData[fieldName];
      setFormSubmissionPayload({ ...filledFormData, elemId: null });
    }
  }, [idsToClear.toggleState]);

  const returnInfoStrings = (option) => {
    switch (option.infoString.dependsOn) {
      case 'memberInfo':
        if (memberInfo) {
          return option.infoString.strings[memberInfo.accountTypes.paymentType]
            ? option.infoString.strings[memberInfo.accountTypes.paymentType]
            : option.infoString.strings.others;
        }
        break;
      case 'static':
        return option.infoString.string;
      default:
        return null;
    }
    return null;
  };

  const showInputBox = (option) => {
    if (option.propsForInput) {
      switch (option.propsForInput.dependsOn) {
        case 'memberInfo':
          if (memberInfo && option.propsForInput.dontShowCurrencyFor
            && (memberInfo.accountTypes.paymentType !== option.propsForInput.dontShowCurrencyFor)) {
            return true;
          }
          break;
        case 'static':
          return true;
        default:
          return false;
      }
    }
    return false;
  };

  const invalidHandler = (e, option, noRadioButtonSelection = false) => {
    if (!noRadioButtonSelection && option && option.propsForInput.hideDefaultValidationMsg) {
      e.preventDefault();
      document.querySelector(`#${option.propsForInput.fieldId}`).focus();
      if (!e.target.value) {
        setCustomInvalidMsg(option.propsForInput.customEmptyInvalidMsg);
      } else {
        setCustomInvalidMsg(option.propsForInput.customInvalidMsg);
      }
      setShowCustomInvalidMsg(true);
      // setStatus('error');
    } else if (noRadioButtonSelection && fieldValidation && fieldValidation.customInvalidMsg) {
      e.preventDefault();
      document.querySelector(`#${option.fieldId}`).focus();
      setValidationError(fieldValidation.customInvalidMsg);
    } else if (fieldRank && errors) {
      // custom validation message --> new implementation
      // disable the default validation message
      e.preventDefault();
      if (fieldValidation.required) {
        setValidationError(errors.mandatoryField);
      }
      setValidationErrors({ ...validationErrors, ...{ [fieldRank]: `${option.fieldId}` } });
      setFocus({ ...validationErrors, ...{ [fieldRank]: `${option.fieldId}` } });
    }
    // invoke some error message red color
  };
  const onInputHandler = (e) => {
    e.target.setCustomValidity('');
    setShowCustomInvalidMsg(false);
    if (validationError) {
      setValidationError('');
      if (fieldRank && validationErrors[fieldRank]) {
        delete validationErrors[fieldRank];
        setValidationErrors({ ...validationErrors });
      }
    }
  };

  const handleKeyPress = (event, regex) => {
    const rex = new RegExp(regex);
    if (!rex.exec(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <>
      {isFieldGrouped ? (
        <div className={cssProperties.style}>
          <fieldset>
            <label
              htmlFor={fieldId}
              className={fieldLabel.cssProperties.styleV2 ? fieldLabel.cssProperties.styleV2
                : `${fieldLabel.cssProperties.style} ${cssProperties.mainLabelClass ? cssProperties.mainLabelClass : 'margin-2-b'}`}
            >
              {parseHtml(fieldLabel.label)}
              <span>  </span>
              {!isSymbolRequired && !fieldValidation.required && <span>{optionalLabel}</span>}
              {fieldValidation && fieldValidation.isToolTip
                && (
                  <Tooltip id="ToolTip" triggerElement={<TooltipButton icon={<IconInfo size="sm" title="info" />} />}>{parseHtml(fieldValidation.toolTipInfo)}</Tooltip>
                )}
            </label>
            <div className={cssProperties.radioMainDivClass ? cssProperties.radioMainDivClass : 'row pad-1-l'}>
              {
                options.map((option) => (
                  <div className={cssProperties.radioDivClass ? cssProperties.radioDivClass : 'radio pad-4-r'} key={option.fieldId}>
                    {option.imgProps && (
                      <div>
                        <img title={option.imgProps.title} alt={option.imgProps.alt} src={option.imgProps.src} style={{ width: `${cssProperties.imgStyle.width}`, height: `${cssProperties.imgStyle.height}` }} />
                      </div>
                    )}
                    <input
                      id={option.fieldId}
                      type="radio"
                      name={fieldName}
                      value={option.value}
                      checked={currentSelectedOption === option.fieldId}
                      required={fieldValidation.required}
                      disabled={option.disabled}
                      onClick={(e) => handleOnclick(e, option)}
                      onChange={() => handleOnChange(option)}
                      onInvalid={(e) => invalidHandler(e, option, true)}
                      onInput={onInputHandler}
                    />
                    <label htmlFor={option.fieldId} className={fieldLabel.cssProperties.style}>
                      {parseHtml(option.fieldLabel)}
                    </label>
                    {
                      option.infoString
                      && (
                        <p className={option.infoString.style}>
                          {returnInfoStrings(option)}
                        </p>
                      )
                    }
                    {
                      showInputBox(option)
                      && (
                        <input
                          type={option.propsForInput.fieldType}
                          onKeyPress={(e) => handleKeyPress(e, option.propsForInput.regex)}
                          step={option.propsForInput.stepValue}
                          id={option.propsForInput.fieldId}
                          name={option.propsForInput.fieldId}
                          disabled={!(currentSelectedOption === option.propsForInput.requiredFor)}
                          title=""
                          min={option.propsForInput.minValue ? option.propsForInput.minValue : ''}
                          max={option.propsForInput.maxValue ? option.propsForInput.maxValue : ''}
                          // pattern="[0-9]"
                          required={currentSelectedOption === option.propsForInput.requiredFor}
                          onInvalid={(e) => invalidHandler(e, option)}
                          onInput={onInputHandler}
                          className={option.propsForInput.cssProperties.style}
                        />
                      )
                    }
                    {showCustomInvalidMsg
                      && option.propsForInput
                      && currentSelectedOption === option.propsForInput.requiredFor
                      && (
                        <div id="alert-error" className={`alert dls-color-warning margin-4-l ${cssProperties.alertErrorStyle}`}>
                          <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
                          <span>{customInvalidMsg}</span>
                        </div>
                      )}
                  </div>
                ))
              }
            </div>
            {validationError
              && (
                <div id="alert-error" className={`alert dls-color-warning pad-1-l ${cssProperties.alertErrorStyle}`}>
                  <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
                  <span>{validationError}</span>
                </div>
              )}
          </fieldset>
        </div>
      )
        : (
          <fieldset>
            <div className="radio">
              <input id={fieldId} type="radio" name={fieldName} value={value} onClick={handleInput} checked={checked} />
              {!checked && isHiddenRequired
                && (
                  <input
                    type="hidden"
                    id={fieldId}
                    name={fieldName}
                    value={value}
                  />
                )}
              <label htmlFor={fieldId} className={fieldLabel.cssProperties.style}>
                {fieldLabel.label}
              </label>
            </div>
          </fieldset>
        )}
      {
        comments && comments[fieldName] && !valueChanged
        && (
          <div id="alert-error" className={`alert dls-color-warning pad-1-t ${cssProperties.alertErrorStyle}`}>
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{comments[fieldName].comment}</span>
          </div>
        )
      }
      {
        currentSelectedOption
        && entityComments.length > 0
        && entityCompositeKey
        && Object.keys(entityComments.filter(
          (obj) => obj.compositeKey === entityCompositeKey)).length > 0
        && Object.keys(entityComments.filter(
          (obj) => obj.compositeKey === entityCompositeKey)[0]).includes(fieldName)
        && (
          <div id="alert-error" className="alert dls-color-warning pad-1-t">
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{
              fieldId === 'compositeKey' ? entityComments.filter((obj) => obj.compositeKey === entityCompositeKey)[0][fieldName]
                : entityComments.filter(
                  (obj) => obj.compositeKey === entityCompositeKey)[0][fieldName].defaultValue
            }
            </span>
          </div>
        )
      }
    </>
  );
};

RadioButton.propTypes = {
  isFieldGrouped: PropTypes.bool,
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  fieldOptions: PropTypes.arrayOf(PropTypes.shape),
  fieldId: PropTypes.string,
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  fieldName: PropTypes.string,
  memberInfo: PropTypes.objectOf(PropTypes.shape),
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  optionsDependentOn: PropTypes.string,
  isSymbolRequired: PropTypes.bool,
  filledFormData: PropTypes.objectOf(PropTypes.shape),
  isHiddenRequired: PropTypes.bool,
  setFormSubmissionPayload: PropTypes.func,
  setShowPopUp: PropTypes.func,
  fieldRank: PropTypes.number,
  setValidationErrors: PropTypes.func,
  validationErrors: PropTypes.objectOf(PropTypes.shape),
  setFocus: PropTypes.func,
  errors: PropTypes.objectOf(PropTypes.shape),
  comments: PropTypes.objectOf(PropTypes.shape),
  setComments: PropTypes.func,
  entityToEdit: PropTypes.objectOf(PropTypes.shape),
  entityComments: PropTypes.objectOf(PropTypes.shape),
  entityCompositeKey: PropTypes.string,
  idsToClear: PropTypes.objectOf(PropTypes.shape),
  formContainer: PropTypes.objectOf(PropTypes.shape),
  curPageValues: PropTypes.objectOf(PropTypes.shape),
  setCurPageValues: PropTypes.func,
  operationsToPerform: PropTypes.objectOf(PropTypes.shape),
  fieldIdsToClear: PropTypes.arrayOf(PropTypes.string),
  dispatchIdsState: PropTypes.func,
  changePageValues: PropTypes.objectOf(PropTypes.shape),
  setChangePageValues: PropTypes.func,
  defaultValue: PropTypes.string,
  entityData: PropTypes.objectOf(PropTypes.shape),
  setEntityData: PropTypes.func,
  keysToUpdate: PropTypes.objectOf(PropTypes.shape),
};

export default RadioButton;
